// Material Symbols Rounded wrapper.
//
// Every UI icon across the platform goes through this component so
// the icon style stays consistent and a future swap (different icon
// set, different weight default, different axis tuning) is a single
// file change.
//
// Usage:
//   <Icon name="home" />
//   <Icon name="mic" size={14} />
//   <Icon name="volume_up" size={20} filled />
//   <Icon name="sports_esports" weight={500} className="text-brand-500" />
//
// NOTE: Use ONLY for UI affordances (nav, buttons, menu items, toggles,
// status glyphs). Do NOT use for game atmosphere (skull, coin rain,
// trophy/defeat graphics, Ludo pawns) — those are intentional
// illustrations and stay as PNGs / emojis. See PROJECT_NOTES.md.
import { CSSProperties } from 'react';

type IconProps = {
  /** Material Symbols identifier — e.g. 'home', 'mic', 'volume_off'. */
  name: string;
  /** Pixel size (controls both font-size and the 'opsz' variation axis). */
  size?: number;
  /** `true` renders the filled variant (FILL=1) instead of outlined. */
  filled?: boolean;
  /** Stroke weight, 100–700 (default 400). */
  weight?: number;
  /** Extra Tailwind / CSS classes. Colour should be set via Tailwind
      `text-*` utilities — the icon inherits `currentColor`. */
  className?: string;
  style?: CSSProperties;
  /** Pass-through aria-label for the parent when the icon is the only
      label for an interactive element. Decorative icons should omit
      this (and a screen-reader-friendly label should sit on the
      parent button/link). */
  ariaLabel?: string;
};

export default function Icon({
  name,
  size = 20,
  filled = false,
  weight = 400,
  className = '',
  style,
  ariaLabel,
}: IconProps) {
  return (
    <span
      className={`material-symbols-rounded select-none leading-none ${className}`}
      style={{
        fontSize: size,
        // Variation axes are tied together here so a caller controls
        // the whole glyph shape through a single prop pair. opsz
        // equals size for crisp rendering at that exact pixel count.
        fontVariationSettings: `'FILL' ${filled ? 1 : 0}, 'wght' ${weight}, 'GRAD' 0, 'opsz' ${size}`,
        ...style,
      }}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    >
      {name}
    </span>
  );
}
